import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { RootState } from '../../../store/root-reducer';
import TrulyInboxLogo from '../../../assets/images/TrulyInbox_Logo.svg';
import { redirectToV2 } from '../../utils/redirect-to-v2';
import './_migration-redirect-overlay.scss';

const MIGRATED_USER_PATH = '/warmup';

const MigrationRedirectOverlay: React.FC = () => {
  const isMigrated = useSelector((state: RootState) => state.home.isMigrated);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const history = useHistory();
  const { pathname } = useLocation();

  const shouldShow = !!isMigrated;

  // Pin migrated users to the email accounts page, even if the URL is changed.
  useEffect(() => {
    if (shouldShow && pathname !== MIGRATED_USER_PATH) {
      history.replace(MIGRATED_USER_PATH);
    }
  }, [shouldShow, pathname, history]);

  useEffect(() => {
    if (!shouldShow) {
      return undefined;
    }

    // Block scrolling and keep keyboard focus on the CTA so nothing
    // behind the overlay is reachable or actionable.
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    buttonRef.current?.focus();

    const trapFocus = (event: KeyboardEvent) => {
      if (event.key === 'Tab') {
        event.preventDefault();
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', trapFocus, true);

    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener('keydown', trapFocus, true);
    };
  }, [shouldShow]);

  if (!shouldShow) {
    return null;
  }

  return (
    <div className="migration-redirect-overlay" role="dialog" aria-modal="true">
      <div className="migration-redirect-overlay__card">
        <img
          className="migration-redirect-overlay__logo"
          src={TrulyInboxLogo}
          alt="TrulyInbox"
        />
        <h1 className="migration-redirect-overlay__title">
          We&apos;ve moved to TrulyInbox V2
        </h1>
        <p className="migration-redirect-overlay__subtitle">
          This app has moved to a new address. Click below to continue.
        </p>
        <button
          ref={buttonRef}
          type="button"
          className="migration-redirect-overlay__button"
          onClick={redirectToV2}
        >
          Go to TrulyInbox V2
        </button>
      </div>
    </div>
  );
};

export default MigrationRedirectOverlay;
